import React from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import Input from 'components/generics/Input';
import './styles/modern_component.style.scss';

export const ModernCard = ({
  title,
  children,
  icon: Icon,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  headerAction,
  headerLeft,
  selectedCulture,
}) => {
  const { t } = useTranslation();
  return (
    <div className="modernCard">
      <div className="cardHeader">
        <div className="cardHeaderContent">
          {Icon && (
            <div className="cardIcon">
              <Icon sx={{ fontSize: 20, color: 'white' }} />
            </div>
          )}
          <h2 className="cardTitle">{title}</h2>
          {headerLeft && <div className="cardHeaderLeft">{headerLeft}</div>}
          <div className="cardHeaderRight">
            {onSearchChange && (
              <div className="cardSearch">
                <SearchIcon className="cardSearchIcon" sx={{ fontSize: 18, color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder={searchPlaceholder || t('common.search')}
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="cardSearchInput"
                />
              </div>
            )}
            {headerAction && <div className="cardHeaderAction">{headerAction}</div>}
          </div>
          {selectedCulture > 0 && (
            <span>
              ({selectedCulture} {t('common.selected')})
            </span>
          )}
        </div>
      </div>
      <div className="cardBody">{children}</div>
    </div>
  );
};

export const ModernCheckbox = ({
  label,
  checked,
  disabled,
  onChange,
  inputType,
  inputLabel,
  constraintName,
  constraintValues,
  onUpdateConstraintValue,
  totalSurface = 0,
}) => {
  const hasInputs = inputType && checked;
  return (
    <label
      className={`modernCheckbox ${hasInputs ? 'checkboxExpanded' : ''} ${disabled ? 'checkboxDisabled' : ''}`}
    >
      <div className="checkboxLeft">
        <div className="checkboxInputWrapper">
          <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className="checkboxInput"
          />
          <div className={`checkboxBox ${checked ? 'checkboxBoxChecked' : ''}`}>
            {checked && <CheckIcon sx={{ fontSize: 12, color: 'white' }} />}
          </div>
        </div>
        <span className="checkboxLabel">{label}</span>
      </div>
      {hasInputs && (
        <div className="checkboxRight">
          <ConstraintInputs
            inputType={inputType}
            inputLabel={inputLabel}
            constraintName={constraintName}
            constraintValues={constraintValues}
            onUpdateConstraintValue={onUpdateConstraintValue}
            totalSurface={totalSurface}
          />
        </div>
      )}
    </label>
  );
};

export const ConstraintInputs = ({
  inputType,
  inputLabel,
  constraintName,
  constraintValues,
  onUpdateConstraintValue,
  totalSurface = 0,
}) => {
  const { t } = useTranslation();
  const values = constraintValues || { min: '', max: '' };
  const currentUnit = values.unit || 'ha';

  const hasUnit = inputType === 'maxWithUnit' || inputType === 'minmaxWithUnit';
  const hasMin = inputType === 'min' || inputType === 'minmax' || inputType === 'minmaxWithUnit';
  const hasMax =
    inputType === 'max' ||
    inputType === 'minmax' ||
    inputType === 'maxWithUnit' ||
    inputType === 'minmaxWithUnit';
  const isSimpleInput = inputType === 'input';

  const calculateHa = (percentValue) => {
    if (!percentValue || !totalSurface) return 0;
    return Math.floor((percentValue * totalSurface) / 100);
  };

  const handleChange = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value) || 0;
    onUpdateConstraintValue(constraintName, field, numValue);
  };

  if (!inputType) return null;

  if (isSimpleInput) {
    return (
      <div className="constraintInputs" onClick={(e) => e.stopPropagation()}>
        <div className="constraintField">
          {inputLabel && <label className="constraintLabel">{inputLabel}</label>}
          <Input
            name={`${constraintName}_min`}
            value={values.min}
            onChange={(val) => handleChange('min', val)}
            type="text"
            dataType="number"
            placeholder="0"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="constraintInputs" onClick={(e) => e.stopPropagation()}>
      <div className="constraintField">
        {inputLabel && <label className="constraintLabel">{inputLabel}</label>}
        {hasUnit && (
          <div className="constraintUnitSelector">
            <button
              type="button"
              className={`constraintUnitBtn ${currentUnit === 'ha' ? 'active' : ''}`}
              onClick={() => onUpdateConstraintValue(constraintName, 'unit', 'ha')}
            >
              HA
            </button>
            <button
              type="button"
              className={`constraintUnitBtn ${currentUnit === 'percent' ? 'active' : ''}`}
              onClick={() => onUpdateConstraintValue(constraintName, 'unit', 'percent')}
            >
              %
            </button>
          </div>
        )}

        {hasMin && (
          <div className="constraintField">
            {!inputLabel && <label className="constraintLabel">{t('common.min')}</label>}
            <Input
              name={`${constraintName}_min`}
              value={values.min}
              onChange={(val) => handleChange('min', val)}
              type="text"
              dataType="number"
              placeholder="0"
            />
            {hasUnit && currentUnit === 'percent' && values.min > 0 && (
              <span className="constraintHaEquiv">({calculateHa(values.min)} ha)</span>
            )}
          </div>
        )}

        {hasMax && (
          <div className="constraintField">
            <label className="constraintLabel">{t('common.max')}</label>
            <Input
              name={`${constraintName}_max`}
              value={values.max}
              onChange={(val) => handleChange('max', val)}
              type="text"
              dataType="number"
              placeholder="0"
            />
            {hasUnit && currentUnit === 'percent' && values.max > 0 && (
              <span className="constraintHaEquiv">({calculateHa(values.max)} ha)</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ModernButton = ({ children, onClick, variant = 'primary', disabled, fullWidth }) => {
  const BUTTON_VARIANT_CLASS = {
    primary: 'modernButtonPrimary',
    secondary: 'modernButtonSecondary',
    accent: 'modernButtonAccent',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`modernButton ${BUTTON_VARIANT_CLASS[variant] ?? ''} ${fullWidth ? 'modernButtonFullWidth' : ''}`}
    >
      {children}
    </button>
  );
};
